/**
 * Per-page metadata.
 *
 * The site is client-rendered, so the tags in `index.html` are what a crawler
 * sees first; these update them in place once the route mounts. Updating rather
 * than appending matters — two `<meta name="description">` tags are worse than
 * one, and the Vercel function that injects the same tags server-side must not
 * end up disagreeing with the app.
 */

export const SITE_URL = 'https://citizensgavel.org';
export const SITE_NAME = "Citizens' Gavel";

/** Used wherever a page has no picture of its own. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/citizens-gavel.png`;

export const DEFAULT_TITLE = "Citizens' Gavel: access to justice in Nigeria";
export const DEFAULT_DESCRIPTION =
    'Citizens’ Gavel uses technology to speed up access to justice in Nigeria. Pro bono legal aid, court performance data and tools that move real cases.';

export interface SeoInput {
    /** The page's own title. The site name is appended unless `bare` is set. */
    title?: string;
    description?: string;
    /** Path or absolute URL. Defaults to the current path. */
    canonical?: string;
    image?: string;
    imageAlt?: string;
    type?: 'website' | 'article';
    /** ISO date — articles and stories only. */
    publishedTime?: string;
    author?: string;
    tags?: string[];
    /** Keeps a page out of the index: search results pages, the 404. */
    noindex?: boolean;
    /** Already carries the site name, so do not append it. */
    bare?: boolean;
}

export const absolute = (value: string): string => {
    if (!value) return '';
    if (/^https?:\/\//.test(value)) return value;
    return `${SITE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
};

/** `Our Blog` → `Our Blog | Citizens' Gavel`, and never past what a result shows. */
export const pageTitle = (title: string | undefined, bare = false): string => {
    const own = (title ?? '').trim();
    if (!own) return DEFAULT_TITLE;
    return bare || own.includes(SITE_NAME) ? own : `${own} | ${SITE_NAME}`;
};

/** Tidied to one line; search results show ~160 characters. */
export const metaDescription = (value: string | undefined): string => {
    const text = (value ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return DEFAULT_DESCRIPTION;
    if (text.length <= 160) return text;
    const room = text.slice(0, 159);
    const lastSpace = room.lastIndexOf(' ');
    return `${(lastSpace > 100 ? room.slice(0, lastSpace) : room).replace(/[\s,;:–—-]+$/, '')}…`;
};
